export class LiquidButtonElement extends HTMLElement {
  public static get observedAttributes() {
    return ['radius', 'variant', 'disabled', 'tint-color', 'tint-opacity', 'blur'];
  }

  private shadowRootRef: ShadowRoot;

  constructor() {
    super();
    this.shadowRootRef = this.attachShadow({ mode: 'open' });
    this.render();
  }

  connectedCallback() {
    this.updateStyles();
  }

  attributeChangedCallback() {
    this.updateStyles();
  }

  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }
  set disabled(val: boolean) {
    if (val) this.setAttribute('disabled', '');
    else this.removeAttribute('disabled');
  }

  get radius(): number {
    return parseFloat(this.getAttribute('radius') || '16');
  }
  set radius(val: number) {
    this.setAttribute('radius', val.toString());
  }

  private updateStyles() {
    const r = this.radius;
    const variant = this.getAttribute('variant') || 'default';
    const blur = this.getAttribute('blur') || '16px';
    const tintOpacity = this.getAttribute('tint-opacity') || (variant === 'primary' ? '0.18' : '0.08');

    const buttonEl = this.shadowRootRef.querySelector('.liquid-btn') as HTMLElement;
    if (buttonEl) {
      buttonEl.style.setProperty('--btn-radius', `${r}px`);
      buttonEl.style.setProperty('--btn-blur', blur.includes('px') ? blur : `${blur}px`);
      buttonEl.style.setProperty('--btn-tint-opacity', tintOpacity);
      if (this.disabled) {
        buttonEl.setAttribute('disabled', '');
      } else {
        buttonEl.removeAttribute('disabled');
      }
    }
  }

  private render() {
    this.shadowRootRef.innerHTML = `
      <style>
        :host {
          display: inline-block;
          vertical-align: middle;
          box-sizing: border-box;
          outline: none;
        }

        .liquid-btn {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 22px;
          border-radius: var(--btn-radius, 16px);
          background: rgba(255, 255, 255, var(--btn-tint-opacity, 0.08));
          backdrop-filter: blur(var(--btn-blur, 16px)) saturate(1.8);
          -webkit-backdrop-filter: blur(var(--btn-blur, 16px)) saturate(1.8);
          border: 1px solid rgba(255, 255, 255, 0.25);
          box-shadow: 
            inset 0 1px 1px 0 rgba(255, 255, 255, 0.45),
            inset 0 -1px 1px 0 rgba(0, 0, 0, 0.15),
            0 4px 16px rgba(0, 0, 0, 0.2);
          color: #ffffff;
          font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif;
          font-size: 14px;
          font-weight: 600;
          letter-spacing: -0.01em;
          cursor: pointer;
          user-select: none;
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          overflow: hidden;
          text-decoration: none;
          box-sizing: border-box;
          width: 100%;
          height: 100%;
        }

        :host([variant="primary"]) .liquid-btn {
          background: rgba(255, 255, 255, 0.18);
          border: 1px solid rgba(255, 255, 255, 0.45);
          box-shadow: 
            inset 0 1px 2px rgba(255, 255, 255, 0.7),
            inset 0 -1px 2px rgba(0, 0, 0, 0.2),
            0 6px 24px rgba(0, 0, 0, 0.25);
        }

        :host([variant="icon"]) .liquid-btn {
          padding: 10px;
          min-width: 42px;
          min-height: 42px;
          border-radius: 50%;
        }

        .liquid-btn:hover {
          background: rgba(255, 255, 255, 0.24);
          border-color: rgba(255, 255, 255, 0.45);
          box-shadow: 
            inset 0 1px 2px rgba(255, 255, 255, 0.6),
            0 8px 28px rgba(0, 0, 0, 0.3);
          transform: translateY(-1px);
        }

        .liquid-btn:active {
          transform: translateY(1px) scale(0.98);
          background: rgba(255, 255, 255, 0.14);
          box-shadow: 
            inset 0 1px 1px rgba(0, 0, 0, 0.3),
            0 2px 8px rgba(0, 0, 0, 0.2);
        }

        .liquid-btn[disabled] {
          opacity: 0.5;
          cursor: not-allowed;
          pointer-events: none;
        }

        .shimmer {
          position: absolute;
          top: 0;
          left: -100%;
          width: 50%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.25),
            transparent
          );
          transform: skewX(-20deg);
          pointer-events: none;
          transition: left 0.6s ease;
        }

        .liquid-btn:hover .shimmer {
          left: 150%;
        }
      </style>
      <button class="liquid-btn" type="button">
        <div class="shimmer"></div>
        <slot></slot>
      </button>
    `;
  }
}
